import React, { useMemo, useState, Suspense } from "react";

import {
  Box,
  Container,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
} from "@mui/material";
import i18next from "i18next";
import { useNavigate } from "react-router-dom";

import { MainLayout } from "@shared/ui/MainLayout";
import MDBox from "@vendor/components/MDBox";
import { getUrlFile } from "@shared/utils/setting";
import { usePermission } from "@shared/hooks/usePermission";
import { userContainPermission } from "@shared/utils/permission";

import { CardPage } from "../components/CardPage";
import { Cover } from "../components/Cover";
import { usePeopleCulture } from "../hooks/usePeopleCulture";

import type { ReservationFlowProps } from "@modules/reservation";

import type { CmsFile } from "../api/cmsApi";

const COVER_FILE = "05_PP_Cover_BE.png";

// ReservationFlow se consume vía el public API del módulo reservation para
// mantener la encapsulación. El dynamic import expone el named export como default.
const ReservationFlow = React.lazy(() =>
  import("@modules/reservation").then((m) => ({ default: m.ReservationFlow }))
);

export const BenefitsPage = () => {
  const { data, isFetching } = usePeopleCulture();
  const { permissionList } = usePermission("Perfil de Imputacion");
  const [openModal, setOpenModal] = useState(false);
  const navigate = useNavigate();

  const cover = useMemo(
    () => (data ? (data.imageCover ?? []).find((image) => image.fileName === COVER_FILE) ?? null : null),
    [data]
  );
  const cardsData = data?.benefitsCardsData ?? null;

  const existPermission = (permission: string) => userContainPermission(permission, permissionList);

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);

  // Al completar la reserva, cierra el modal y navega a gestión de reservas.
  const handleReservationCompleted = () => {
    setOpenModal(false);
    navigate("/gestion-de-reservas");
  };

  const getFile = (file?: CmsFile) =>
    file ? getUrlFile(file.fileId as string, file.extension as string) : null;

  return (
    <MainLayout loading={isFetching}>
      <Container>
        {cover && <Cover getFile={getFile} fileName={cover} name="Beneficios" />}
        <MDBox py={3}>
          <Grid container spacing={3}>
            <Grid size={12}>
              <Box>
                <Grid container spacing={3}>
                  {cardsData &&
                    cardsData.map(
                      (card, index) =>
                        existPermission(card.permission ?? "") && (
                          <Grid
                            key={index}
                            size={{
                              xs: 12,
                              md: 6,
                              lg: 4,
                            }}
                          >
                            <CardPage
                              title={card.title}
                              image={getFile(card.image)}
                              content={card.text}
                              textFirst={i18next.t("policies")}
                              urlFirst={card.link}
                              textSecond={i18next.t("obtain")}
                              onSecondClick={
                                card.title === "CODERLAND HOME'S" ? handleOpenModal : null
                              }
                              urlSecond="#/people-and-culture"
                            />
                          </Grid>
                        )
                    )}
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </MDBox>

        <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
          <DialogTitle>{i18next.t("coderlandHomeModalTitle")}</DialogTitle>
          <DialogContent>
            <Suspense fallback={<CircularProgress />}>
              <ReservationFlow
                handleCloseModal={handleCloseModal}
                mode="create"
                // BUG conocido: no se pasa `vacationHomes` y ReservationFlow le hace `.find()`,
                // asi que el flujo falla. reservation esta congelado y fuera de uso: el cast solo
                // conserva el comportamiento de siempre (la prop llega `undefined`).
                {...({} as Pick<ReservationFlowProps, "vacationHomes">)}
                onReservationCompleted={handleReservationCompleted}
              />
            </Suspense>
          </DialogContent>
        </Dialog>
      </Container>
    </MainLayout>
  );
};
